/* The portal's pages, rendered from the example dataset – the same composition as the real pages, laid out by the
   Stage's mode (full · compact · narrow) instead of viewport breakpoints. Inert: shown, not used. */
import { ArrowLeft, ChevronRight, MessageSquarePlus, Zap } from "lucide-react";
import { TodayPowerChart } from "@/components/charts/TodayPowerChart";
import { ProductionChart } from "@/components/charts/ProductionChart";
import { LensMark } from "@/components/brand/ProximaLogo";
import { durationLabel, fmtDayTime, fmtFlow, fmtHours, fmtInt, fmtLevel, fmtMW, fmtMWh, fmtNOK, fmtNum, fmtPct, fmtPrice, fmtTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { AS_OF_LABEL, CUSTOMER, EVENTS, EVENT_COUNTS, FOLLOW_UP, HAUGELAND, HAUGELAND_EVENTS, HAUGELAND_EVENT_ROWS, HAUGELAND_SERVICE, HAUGELAND_SUMMARY, HAUGELAND_WEEK, PLANTS, PORTFOLIO, SERIES_TODAY, SERIES_YESTERDAY } from "@/data/tilbod/hhDemo";
import { Btn, Chip, DeltaText, ExportBtn, Figure, LabelCaps, LineTabs, Meta, MoreMeasurements, N, PageTitle, Pill, RangePicker, SearchBox, Segmented, SelectStub, Severity, StatusWord, TextLink } from "./ui";

const ORDER = { stopped: 0, maintenance: 1, reduced: 2, running: 3 };
const sorted = [...PLANTS].sort((a, b) => ORDER[a.status] - ORDER[b.status] || (b.events_open || 0) - (a.events_open || 0) || a.name.localeCompare(b.name, "nb"));
const short = (name) => name.replace(/ kraftverk$/i, "");
const energyCell = (v) => (Math.abs(v) >= 10000 ? `${fmtNum(v / 1000, 1)} GWh` : `${fmtNum(v, 1)} MWh`);
const reason = (p) => (p.status === "reduced" ? p.status_reason?.replace(/^redusert drift\s*[–-]\s*/i, "") : p.status_reason);
const Swatch = ({ color, dashed }) => <span aria-hidden="true" className={cn("inline-block h-0 w-4 border-t-2", dashed ? "border-dashed" : "border-solid")} style={{ borderColor: color }} />;
const BLUE = "hsl(208 91% 36%)";
const GREY = "hsl(210 14% 76%)";

const Explain = ({ children }) => (
  <span className="inline-flex h-8 items-center gap-2 px-2 text-[13px] text-muted-foreground">
    <LensMark className="h-3.5 w-3.5" />
    {children}
  </span>
);

/* ---------- the plant table (Oversikt + Kraftverk) ---------- */
export const PlantTable = ({ mode, municipality = false, limit }) => {
  const rows = limit ? sorted.slice(0, limit) : sorted;
  if (mode === "narrow") {
    return (
      <ul className="divide-y divide-border">
        {rows.map((p) => (
          <li key={p.id} className="flex items-start justify-between gap-3 py-3">
            <div className="min-w-0">
              <div className="text-[15px] font-semibold leading-snug">{short(p.name)}</div>
              <div className="mt-1">
                <StatusWord status={p.status} reason={reason(p)} size="sm" />
              </div>
              {municipality ? <div className="mt-0.5 text-[13px] text-muted-foreground">{p.municipality}</div> : null}
            </div>
            <div className="shrink-0 text-right">
              <span className="metric whitespace-nowrap text-[14.5px]">
                <span className={cn("font-medium", p.status === "stopped" ? "text-rose-700" : "text-foreground")}>{fmtNum(p.power_mw, p.capacity_mw < 1 ? 2 : 1)}</span>
                <span className="text-muted-foreground"> / {fmtNum(p.capacity_mw, p.capacity_mw < 1 ? 2 : 1)} MW</span>
              </span>
              <div className="metric mt-0.5 text-[13px] text-muted-foreground">{fmtMWh(p.energy_today_mwh)} i dag</div>
              {p.events_open ? <div className={cn("mt-0.5 text-[13px] font-medium", p.events_ongoing ? (p.events_critical ? "text-rose-700" : "text-amber-700") : "text-foreground")}>{p.events_open} hendelse{p.events_ongoing ? "" : " · avsluttet"}</div> : null}
            </div>
          </li>
        ))}
      </ul>
    );
  }
  const th = "py-2.5 font-medium";
  return (
    <table className="w-full border-collapse text-[14.5px]">
      <thead>
        <tr className="border-b border-border text-left text-[13px] text-muted-foreground">
          <th className={cn(th, "w-[24%] pr-4")}>Kraftverk</th>
          <th className={cn(th, "w-[14%] pr-4 text-right")}>Effekt nå</th>
          <th className={cn(th, "w-[10%] pr-4 text-right")}>Utnyttelse</th>
          {mode === "full" ? <th className={cn(th, "w-[13%] pr-4 text-right")}>Produsert i dag</th> : null}
          <th className={cn(th, "pr-4")}>Status</th>
          <th className={cn(th, "w-[9%] text-right")}>Hendelser</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((p) => {
          const d = p.capacity_mw < 1 ? 2 : 1;
          return (
            <tr key={p.id} className="border-b border-border">
              <td className="py-3 pr-4 align-middle">
                <span className="text-[15px] font-semibold leading-snug text-foreground">{short(p.name)}</span>
                {municipality ? <span className="ml-2 text-[13px] text-muted-foreground">{p.municipality}</span> : null}
              </td>
              <td className="py-3 pr-4 text-right align-middle">
                <span className="metric whitespace-nowrap">
                  <span className={cn("font-medium", p.status === "stopped" ? "text-rose-700" : "text-foreground")}>{fmtNum(p.power_mw, d)}</span>
                  <span className="text-muted-foreground"> / {fmtNum(p.capacity_mw, d)} MW</span>
                </span>
              </td>
              <td className={cn("metric py-3 pr-4 text-right align-middle", p.status === "running" ? "text-foreground" : "text-muted-foreground")}>{p.utilisation_pct} %</td>
              {mode === "full" ? <td className="metric py-3 pr-4 text-right align-middle">{energyCell(p.energy_today_mwh)}</td> : null}
              <td className="max-w-0 py-3 pr-4 align-middle">
                <StatusWord status={p.status} reason={reason(p)} />
              </td>
              <td className="py-3 text-right align-middle">
                {p.events_open ? <span className={cn("metric font-semibold", p.events_ongoing ? (p.events_critical ? "text-rose-700" : "text-amber-700") : "text-foreground")}>{p.events_open}</span> : <span className="text-muted-foreground/50">–</span>}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

/* ---------- Oversikt ---------- */
const FollowUp = ({ items = FOLLOW_UP }) => (
  <ul className="divide-y divide-border">
    {items.map((f) => (
      <li key={f.id} className="py-3 first:pt-0">
        <div className="flex items-baseline justify-between gap-3">
          <span className="truncate text-[15px] font-semibold leading-snug text-foreground">{f.name}</span>
          <span className={cn("shrink-0 text-[13px] font-medium", f.tone === "stopped" ? "text-rose-700" : f.tone === "reduced" ? "text-amber-700" : "text-muted-foreground")}>{f.word}</span>
        </div>
        <div className="mt-0.5 text-[14px] leading-snug text-foreground">{f.reason}</div>
        <div className="metric mt-0.5 text-[13px] leading-snug text-muted-foreground">{f.meta}</div>
        {f.link ? <div className="mt-1.5 text-[13px] font-medium text-primary">Se hendelsen →</div> : null}
      </li>
    ))}
  </ul>
);

export const Oversikt = ({ mode }) => {
  const k = PORTFOLIO;
  const narrow = mode === "narrow";
  const chartH = mode === "full" ? 340 : mode === "compact" ? 270 : 230;
  return (
    <div className={narrow ? "space-y-8" : "space-y-10"}>
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
        <div className="min-w-0">
          <PageTitle>{CUSTOMER.short_name}</PageTitle>
          <Meta>
            <N>{k.plants_total}</N> kraftverk · <N>{fmtNum(k.capacity_mw, 1)}</N> MW installert
          </Meta>
        </div>
        <div className="text-[13.5px] text-muted-foreground">
          Målinger kl. <N>{AS_OF_LABEL}</N>
        </div>
      </div>

      <div className={cn("grid", mode === "full" ? "grid-cols-[minmax(0,1fr)_300px] gap-14" : mode === "compact" ? "grid-cols-[minmax(0,1fr)_260px] gap-10" : "grid-cols-1 gap-8")}>
        <div className="min-w-0">
          <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
            <div className="min-w-0">
              <LabelCaps>Produksjon</LabelCaps>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="metric text-[40px] font-semibold leading-none tracking-[-0.03em] text-foreground">{fmtNum(k.power_now_mw, 1)}</span>
                <span className="text-[18px] font-medium text-muted-foreground">MW</span>
              </div>
              <div className="mt-2 text-[14.5px] leading-snug text-muted-foreground">
                <N>{k.utilisation_pct} %</N> av installert effekt · <N>{k.plants_running} av {k.plants_total}</N> produserer
              </div>
            </div>
            <Segmented items={["I dag", "7 dager", "30 dager"]} value="I dag" />
          </div>
          <div className="mt-5">
            <TodayPowerChart today={SERIES_TODAY} yesterday={SERIES_YESTERDAY} capacity={k.capacity_mw} height={chartH} />
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-x-5 gap-y-1 text-[13px] text-muted-foreground">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
              <span className="inline-flex items-center gap-2"><Swatch color={BLUE} /> I dag</span>
              <span className="inline-flex items-center gap-2"><Swatch color={GREY} /> I går</span>
              <span>
                Målt til kl. <N>{AS_OF_LABEL}</N> · dagen er ikke ferdig
              </span>
            </div>
            <Explain>Forklar produksjonsfallet</Explain>
          </div>
          <div className={cn("mt-8 grid gap-x-8 gap-y-6 border-t border-border pt-5", narrow ? "grid-cols-2" : "grid-cols-3")}>
            <Figure label="Produsert i dag" value={fmtNum(k.energy_today_mwh, 0)} unit="MWh" sub={<span className="inline-flex items-baseline gap-1.5"><DeltaText value={k.energy_today_delta_pct} /> mot i går på samme tid</span>} />
            <Figure label="Est. inntekt i dag" value={fmtNOK(k.revenue_today_nok)} sub={<>Spotpris nå <N>{fmtPrice(k.spot_now_nok_mwh)}</N></>} />
            <Figure label="Tilgjengelighet, 30 d" value={fmtNum(k.availability_30d_pct, 1)} unit="%" sub={<><N>{fmtMWh(k.energy_30d_mwh)}</N> produsert</>} />
          </div>
        </div>
        <div className="min-w-0">
          <div className="flex h-7 items-baseline justify-between gap-3">
            <LabelCaps>
              Krever oppfølging <span className="ml-1.5 text-foreground">· {FOLLOW_UP.length}</span>
            </LabelCaps>
            <TextLink>Alle hendelser</TextLink>
          </div>
          <div className="mt-3">
            <FollowUp items={narrow ? FOLLOW_UP.slice(0, 2) : FOLLOW_UP} />
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-baseline justify-between gap-3">
          <LabelCaps>
            Kraftverk <span className="ml-1.5 text-foreground">· {PLANTS.length}</span>
          </LabelCaps>
          <TextLink>Filtre og kart</TextLink>
        </div>
        <div className="mt-3">
          <PlantTable mode={mode} limit={12} />
        </div>
      </div>
    </div>
  );
};

/* ---------- Kraftverk ---------- */
export const Kraftverk = ({ mode }) => {
  const narrow = mode === "narrow";
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <PageTitle>Kraftverk</PageTitle>
          <Meta>
            <N>{PLANTS.length}</N> kraftverk · <N>{fmtNum(PORTFOLIO.power_now_mw, 1)}</N> av <N>{fmtNum(PORTFOLIO.capacity_mw, 1)}</N> MW produseres nå
          </Meta>
        </div>
        <Segmented items={["Liste", "Kart"]} value="Liste" />
      </div>
      <div className="flex flex-wrap items-center gap-2 border-b border-border pb-4">
        <SearchBox placeholder="Søk kraftverk eller kommune" className={narrow ? "w-full" : "w-72"} />
        <div className="flex flex-wrap items-center gap-0.5">
          <Chip count={24}>I drift</Chip>
          <Chip count={1}>Redusert</Chip>
          <Chip count={1}>Stanset</Chip>
          <Chip count={0}>Vedlikehold</Chip>
        </div>
      </div>
      <PlantTable mode={mode} municipality />
    </div>
  );
};

/* ---------- Ett kraftverk: Haugeland ---------- */
const Summary = ({ label, value }) => (
  <div className="min-w-0">
    <div className="text-[13px] text-muted-foreground">{label}</div>
    <div className="metric mt-1 text-[16px] font-medium leading-none text-foreground">{value}</div>
  </div>
);
const LiveRow = ({ label, value, sub }) => (
  <div className="flex items-start justify-between gap-3 py-2.5">
    <span className="text-[13.5px] text-muted-foreground">{label}</span>
    <span className="text-right">
      <span className="metric text-[14.5px] font-medium">{value}</span>
      {sub ? <div className="metric text-[12px] text-muted-foreground">{sub}</div> : null}
    </span>
  </div>
);
const endedLabel = (e) => (!e.ended_at ? "pågår" : `avsluttet ${fmtTime(e.ended_at)}`);

export const KraftverkDetalj = ({ mode }) => {
  const p = HAUGELAND;
  const k = p.kpis;
  const s = HAUGELAND_SUMMARY;
  const narrow = mode === "narrow";
  const chartH = mode === "full" ? 380 : mode === "compact" ? 290 : 240;
  const levelPct = Math.min(100, Math.max(0, ((p.level_m - p.intake_level_min_m) / (p.intake_level_max_m - p.intake_level_min_m)) * 100));
  return (
    <div className={narrow ? "space-y-8" : "space-y-10"}>
      <div>
        <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-muted-foreground">
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> Kraftverk
        </span>
        <div className={cn("mt-3 flex gap-4", narrow ? "flex-col" : "flex-row items-start justify-between")}>
          <div className="min-w-0">
            <PageTitle>{p.name}</PageTitle>
            <Meta>
              {p.municipality} · <N>{fmtNum(p.capacity_mw, 1)}</N> MW installert · {p.price_area}
            </Meta>
            <div className="mt-3 text-[17px] font-medium leading-snug text-rose-600">
              Stanset siden kl. {fmtTime(p.stopped_since)} · {p.status_reason}
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2 pt-1">
            <Btn variant="outline">Se hendelsen</Btn>
            <Btn variant="primary" icon={MessageSquarePlus}>Kontakt Proxima</Btn>
          </div>
        </div>
      </div>

      <div>
        <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
          <div className="min-w-0">
            <LabelCaps>Produksjon</LabelCaps>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="metric text-[40px] font-semibold leading-none tracking-[-0.03em] text-rose-700">0,0</span>
              <span className="text-[18px] font-medium text-muted-foreground">MW</span>
            </div>
            <div className="mt-2 text-[14.5px] text-muted-foreground">
              <N>0 %</N> av <N>{fmtNum(p.capacity_mw, 1)}</N> MW installert · siste måling kl. <N>{AS_OF_LABEL}</N>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <RangePicker value="7 d" custom={!narrow} />
            <MoreMeasurements />
            <ExportBtn />
          </div>
        </div>
        <div className="mt-5">
          <ProductionChart series={HAUGELAND_WEEK} resolution="hour" spanDays={7} capacity={p.capacity_mw} events={HAUGELAND_EVENTS} height={chartH} />
        </div>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-x-5 gap-y-1 text-[13px] text-muted-foreground">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
            <span className="inline-flex items-center gap-2"><Swatch color={BLUE} /> Effekt, MW</span>
            <span>
              {fmtDayTime(HAUGELAND_WEEK[0].ts)} – {fmtDayTime(HAUGELAND_WEEK[HAUGELAND_WEEK.length - 1].ts)} · 2 hendelser i perioden
            </span>
          </div>
          <Explain>Forklar produksjonsfallet</Explain>
        </div>
        <div className={cn("mt-6 grid gap-x-8 gap-y-5 border-t border-border pt-5", narrow ? "grid-cols-2" : mode === "compact" ? "grid-cols-3" : "grid-cols-6")}>
          <Summary label="Produksjon i perioden" value={fmtMWh(s.energy_mwh)} />
          <Summary label="Snitt effekt" value={fmtMW(s.avg_power_mw, 2)} />
          <Summary label="Maks effekt" value={fmtMW(s.max_power_mw, 2)} />
          <Summary label="Kapasitetsfaktor" value={fmtPct(s.capacity_factor_pct, 0)} />
          <Summary label="Timer stanset" value={fmtHours(s.hours_stopped)} />
          <Summary label="Est. inntekt i perioden" value={fmtNOK(s.revenue_nok)} />
        </div>
      </div>

      <div className={cn("grid gap-x-8 gap-y-6 border-t border-border pt-6", narrow ? "grid-cols-2" : "grid-cols-4")}>
        <Figure label="Produsert i dag" value={fmtNum(k.energy_today_mwh, 1)} unit="MWh" sub={<span className="inline-flex items-baseline gap-1.5"><DeltaText value={k.energy_today_delta_pct} /> mot i går</span>} />
        <Figure label="Siste 30 dager" value={fmtInt(k.energy_30d_mwh)} unit="MWh" sub={<span className="inline-flex items-baseline gap-1.5"><DeltaText value={k.energy_30d_delta_pct} /> mot forrige 30 dager</span>} />
        <Figure label="Tilgjengelighet, 30 d" value={fmtNum(k.availability_30d_pct, 1)} unit="%" sub={<><N>{fmtHours(k.hours_stopped_30d)}</N> stanset · kapasitetsfaktor <N>{fmtPct(k.capacity_factor_30d_pct, 0)}</N></>} />
        <Figure label="Est. inntekt, 30 d" value={fmtNOK(k.revenue_30d_nok)} sub={<>Snittpris <N>{fmtPrice(k.avg_spot_30d_nok_mwh)}</N></>} />
      </div>

      <div className={cn("grid gap-10 border-t border-border pt-8", mode === "full" ? "grid-cols-12 gap-14" : "grid-cols-1")}>
        <div className={cn("min-w-0", mode === "full" && "col-span-8")}>
          <LineTabs items={[["Hendelser", 37], ["Service"], ["Teknisk informasjon"]]} value="Hendelser" />
          <table className="mt-3 w-full text-[14px]">
            <thead>
              <tr className="border-b border-border text-left text-[13px] text-muted-foreground">
                <th className="w-[110px] py-2.5 font-medium">Alvor</th>
                <th className="py-2.5 font-medium">Hendelse</th>
                {!narrow ? <th className="w-[170px] py-2.5 font-medium">Start</th> : null}
                {mode === "full" ? <th className="w-[150px] py-2.5 font-medium">Varighet</th> : null}
                <th className="w-[110px] py-2.5 font-medium">Status</th>
                <th className="w-[36px] py-2.5" />
              </tr>
            </thead>
            <tbody>
              {HAUGELAND_EVENT_ROWS.map((e) => (
                <tr key={e.id} className="border-b border-border">
                  <td className="py-3 align-middle"><Severity severity={e.severity} /></td>
                  <td className="py-3 align-middle">
                    <div className="flex items-center gap-2 font-medium">
                      {e.title}
                      {e.production_stop ? <Zap className="h-3 w-3 text-amber-600" aria-hidden="true" /> : null}
                    </div>
                    <div className="text-[11.5px] text-muted-foreground">{e.kind_label}</div>
                  </td>
                  {!narrow ? (
                    <td className="py-3 align-middle">
                      <div className="metric text-[11.5px]">{fmtDayTime(e.started_at)}</div>
                      <div className="text-[11px] text-muted-foreground">{e.id === "ev-haug-1410" ? "2 timer siden" : e.id === "ev-haug-1010" ? "4 dager siden" : e.id === "ev-haug-0310" ? "11 dager siden" : e.id === "ev-haug-0909" ? "en måned siden" : "2 måneder siden"}</div>
                    </td>
                  ) : null}
                  {mode === "full" ? (
                    <td className="py-3 align-middle">
                      <div className="metric text-[12.5px]">{durationLabel(e.duration_h)}</div>
                      <div className={cn("whitespace-nowrap text-[11px]", !e.ended_at ? "font-medium text-foreground" : "text-muted-foreground")}>{endedLabel(e)}</div>
                    </td>
                  ) : null}
                  <td className="py-3 align-middle"><Pill status={e.status} /></td>
                  <td className="py-3 align-middle"><ChevronRight className="h-4 w-4 text-muted-foreground/40" aria-hidden="true" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {mode === "full" ? (
          <div className="col-span-4 min-w-0 space-y-9">
            <div>
              <div className="flex items-baseline justify-between gap-3">
                <LabelCaps>Nå-verdier</LabelCaps>
                <span className="text-[12.5px] text-muted-foreground">Målt kl. <N>{AS_OF_LABEL}</N></span>
              </div>
              <div className="mt-2 divide-y divide-border">
                <LiveRow label="Vannføring i elv" value={fmtFlow(p.flow_m3s)} sub={`Slukeevne ${fmtFlow(p.design_flow_m3s)}`} />
                <LiveRow label="Gjennom turbin" value={fmtFlow(p.turbine_flow_m3s)} />
                <div className="py-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[13.5px] text-muted-foreground">Vannstand inntak</span>
                    <span className="metric text-[14.5px] font-medium">{fmtLevel(p.level_m)}</span>
                  </div>
                  <div className="util-track mt-2.5">
                    <div className="util-fill bg-chart-3" style={{ width: `${levelPct}%` }} />
                  </div>
                  <div className="metric mt-1.5 flex justify-between text-[11.5px] text-muted-foreground">
                    <span>LRV {fmtNum(p.intake_level_min_m, 1)}</span>
                    <span>HRV {fmtNum(p.intake_level_max_m, 1)}</span>
                  </div>
                </div>
                <LiveRow label={`Spotpris ${p.price_area}`} value={fmtPrice(p.spot_nok_mwh)} sub={`${fmtInt(p.spot_nok_mwh)} NOK/MWh`} />
                <LiveRow label="Produsert i dag" value={fmtMWh(p.energy_today_mwh)} sub={fmtNOK(p.revenue_today_nok)} />
              </div>
            </div>
            <div>
              <div className="flex items-baseline justify-between gap-3">
                <LabelCaps>Åpne hendelser</LabelCaps>
                <span className="text-[12.5px] text-muted-foreground">1 venter på oppfølging</span>
              </div>
              <div className="mt-2 flex items-start gap-3 py-2.5">
                <span className="status-dot mt-[7px] bg-rose-500" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-medium">{p.status_reason}</div>
                  <div className="metric mt-0.5 text-[12.5px] text-muted-foreground">{fmtDayTime(p.stopped_since)} · pågår · ikke kvittert</div>
                </div>
              </div>
            </div>
            <div>
              <LabelCaps>Siste service</LabelCaps>
              <div className="mt-2 text-[14px] font-medium">{HAUGELAND_SERVICE.type}</div>
              <div className="metric mt-0.5 text-[12.5px] text-muted-foreground">12. apr. 2026 · {HAUGELAND_SERVICE.technician} · {HAUGELAND_SERVICE.report_ref}</div>
              <p className="mt-2 text-[13.5px] leading-relaxed text-muted-foreground">{HAUGELAND_SERVICE.description}</p>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

/* ---------- Hendelser ---------- */
export const Hendelser = ({ mode }) => {
  const narrow = mode === "narrow";
  const c = EVENT_COUNTS;
  return (
    <div className="space-y-6">
      <div>
        <PageTitle>Hendelser</PageTitle>
        <Meta>
          <N className="font-medium text-foreground">{c.open}</N> åpne ikke kvittert · <N className="font-medium text-rose-700">{c.critical_open}</N> kritiske aktive · <N>{c.last_30d}</N> hendelser siste 30 dager, hvorav <N>{c.stops_30d}</N> med produksjonsstans
        </Meta>
      </div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-2 border-b border-border pb-4">
        <SearchBox placeholder="Søk i hendelser" className={narrow ? "w-full" : "w-64"} />
        <div className="flex items-center gap-0.5">
          <Chip><span className="status-dot bg-rose-500" aria-hidden="true" /> Kritisk</Chip>
          <Chip><span className="status-dot bg-amber-500" aria-hidden="true" /> Advarsel</Chip>
          <Chip><span className="status-dot bg-slate-400" aria-hidden="true" /> Info</Chip>
        </div>
        {!narrow ? (
          <div className="ml-auto flex flex-wrap items-center gap-0.5">
            <SelectStub>Alle statuser</SelectStub>
            <SelectStub>Alle typer</SelectStub>
            <SelectStub>Alle kraftverk</SelectStub>
            <SelectStub>Siste 30 dager</SelectStub>
          </div>
        ) : null}
      </div>
      {narrow ? (
        <ul className="divide-y divide-border">
          {EVENTS.map((e) => (
            <li key={e.id} className="flex items-start gap-3 py-3">
              <span className={cn("status-dot mt-[7px]", e.severity === "critical" ? "bg-rose-500" : e.severity === "warning" ? "bg-amber-500" : "bg-slate-400")} aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <div className="text-[13.5px] font-medium leading-snug">{e.title}</div>
                <div className="mono mt-1 text-[10.5px] text-muted-foreground">
                  {e.plant_name} · {fmtDayTime(e.started_at)} · {durationLabel(e.duration_h)} · <span className={cn(!e.ended_at && "font-medium text-foreground")}>{endedLabel(e)}</span>
                </div>
                <div className="mt-2 flex items-center gap-2.5">
                  <Severity severity={e.severity} />
                  <Pill status={e.status} />
                </div>
              </div>
              <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground/40" aria-hidden="true" />
            </li>
          ))}
        </ul>
      ) : (
        <table className="w-full text-[14px]">
          <thead>
            <tr className="border-b border-border text-left text-[13px] text-muted-foreground">
              <th className="w-[110px] py-2.5 font-medium">Alvor</th>
              <th className="py-2.5 font-medium">Hendelse</th>
              <th className="py-2.5 font-medium">Kraftverk</th>
              <th className="w-[170px] py-2.5 font-medium">Start</th>
              <th className="w-[150px] py-2.5 font-medium">Varighet</th>
              <th className="w-[110px] py-2.5 font-medium">Status</th>
              <th className="w-[36px] py-2.5" />
            </tr>
          </thead>
          <tbody>
            {EVENTS.map((e) => (
              <tr key={e.id} className="border-b border-border">
                <td className="py-3 align-middle"><Severity severity={e.severity} /></td>
                <td className="py-3 align-middle">
                  <div className="flex items-center gap-2 font-medium">
                    {e.title}
                    {e.production_stop ? <Zap className="h-3 w-3 text-amber-600" aria-hidden="true" /> : null}
                  </div>
                  <div className="text-[11.5px] text-muted-foreground">{e.kind_label}</div>
                </td>
                <td className="py-3 align-middle text-[13px]">{e.plant_name}</td>
                <td className="py-3 align-middle">
                  <div className="metric text-[11.5px]">{fmtDayTime(e.started_at)}</div>
                  <div className="text-[11px] text-muted-foreground">{e.rel}</div>
                </td>
                <td className="py-3 align-middle">
                  <div className="metric text-[12.5px]">{durationLabel(e.duration_h)}</div>
                  <div className={cn("whitespace-nowrap text-[11px]", !e.ended_at ? "font-medium text-foreground" : "text-muted-foreground")}>{endedLabel(e)}</div>
                </td>
                <td className="py-3 align-middle"><Pill status={e.status} /></td>
                <td className="py-3 align-middle"><ChevronRight className="h-4 w-4 text-muted-foreground/40" aria-hidden="true" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="metric text-[13px] text-muted-foreground">Viser {EVENTS.length} av {c.last_30d} hendelser</div>
    </div>
  );
};
